/**
 * Vault – secure storage. Doc: docs/overall-plan/02-MANGO-PERSONAL/vault
 * Wired to GET/POST/PUT/DELETE /api/v1/vault/items.
 */
import React, { useEffect, useState } from 'react';
import { vaultApi, type VaultItem } from '../services/api';

const Vault: React.FC = () => {
  const [items, setItems] = useState<VaultItem[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'document' | 'credential'>('document');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<'document' | 'credential'>('document');
  const [editDescription, setEditDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadItems = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await vaultApi.getItems();
      if (res?.success) {
        setItems(Array.isArray(res?.data) ? res.data : []);
        setMessage(res?.message ?? null);
      } else setError(res?.error || 'Failed to load vault');
    } catch {
      setError('Failed to load vault');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await vaultApi.createItem({ name: newName.trim(), type: newType });
      if (res?.success) {
        setNewName('');
        setShowAdd(false);
        loadItems(true);
      } else setError(res?.error || 'Failed to add item');
    } catch {
      setError('Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item: VaultItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditType((item.type as 'document' | 'credential') || 'document');
    setEditDescription(item.description || '');
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await vaultApi.updateItem(editingId, {
        name: editName.trim(),
        type: editType,
        description: editDescription.trim() || undefined,
      });
      if (res?.success) {
        setEditingId(null);
        loadItems(true);
      } else setError(res?.error || 'Failed to update');
    } catch {
      setError('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    setError(null);
    try {
      const res = await vaultApi.deleteItem(id);
      if (res?.success) loadItems(true);
      else setError(res?.error || 'Failed to delete');
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🔒 Vault</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Secure storage for sensitive documents and credentials
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{
                padding: '8px 16px',
                background: 'rgba(76,175,80,0.9)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add Item
            </button>
            <button
              type="button"
              onClick={() => loadItems(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          <input
            type="text"
            placeholder="Item name (e.g. Passport, Insurance)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as 'document' | 'credential')}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            <option value="document">Document</option>
            <option value="credential">Credential</option>
          </select>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAdd}
              disabled={submitting || !newName.trim()}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewName(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingId && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '2px solid #3b82f6',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Edit item</h3>
          <input
            type="text"
            placeholder="Item name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <select
            value={editType}
            onChange={(e) => setEditType(e.target.value as 'document' | 'credential')}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            <option value="document">Document</option>
            <option value="credential">Credential</option>
          </select>
          <input
            type="text"
            placeholder="Description (optional)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={submitting || !editName.trim()}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditingId(null); setEditName(''); setEditDescription(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && items.length === 0 && (
        <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>
      )}
      {items.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          No items yet. Add a document or credential above.
        </div>
      )}
      {items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item) => (
            <li
              key={item.id}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div>
                <strong>{item.name}</strong>
                {item.type && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>
                    ({item.type})
                  </span>
                )}
                {item.description && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{item.description}</p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleEdit(item)}
                  style={{
                    padding: '6px 12px',
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item.id)}
                disabled={deleting === item.id}
                style={{
                  padding: '6px 12px',
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: deleting === item.id ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                }}
              >
                {deleting === item.id ? '…' : 'Delete'}
              </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Vault;
